import React, { FC, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  IconButton,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Variant } from '../../interface/interface';

// Define the PublishStatus enum to match the backend
export enum PublishStatus {
  ACTIVE = 'ACTIVE',
  DELISTED = 'DELISTED',
  ARCHIVED = 'ARCHIVED',
  DRAFT = 'DRAFT'
}

// Define the props for the PublishedStatusDialog component
interface PublishedStatusDialogProps {
  open: boolean;
  onClose: () => void;
  variant: Variant;
  onUpdate: (variantId: string, status: PublishStatus) => Promise<void>;
}

// Dialog component for editing the published status
const PublishedStatusDialog: FC<PublishedStatusDialogProps> = ({
  open,
  onClose,
  variant,
  onUpdate
}) => {
  if (!variant) {
    return null; // Return early if variant is null
  }

  // Use variant.status directly, defaulting to DRAFT if not available
  const [status, setStatus] = useState<PublishStatus>(
    (variant.status as PublishStatus) || PublishStatus.DRAFT
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Update the status if the variant changes
  useEffect(() => {
    if (variant && variant.status) {
      setStatus(variant.status as PublishStatus);
    }
  }, [variant]);

  const handleUpdate = async () => {
    setLoading(true);
    setError(null);

    try {
      await onUpdate(variant.id, status);
      setLoading(false);
      onClose(); // Close the dialog after successful update
    } catch (err) {
      console.error('Failed to update published status:', err);
      setError('Failed to update status. Please try again.');
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Update Product Variant Status</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500]
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers>
        <FormControl fullWidth>
          <InputLabel id="published-status-label">
            Publication Status
          </InputLabel>
          <Select
            labelId="published-status-label"
            value={status}
            label="Publication Status"
            onChange={(e) => setStatus(e.target.value as PublishStatus)}
          >
            <MenuItem value={PublishStatus.ACTIVE}>ACTIVE</MenuItem>
            <MenuItem value={PublishStatus.DELISTED}>DELISTED</MenuItem>
            <MenuItem value={PublishStatus.ARCHIVED}>ARCHIVED</MenuItem>
            <MenuItem value={PublishStatus.DRAFT}>DRAFT</MenuItem>
          </Select>
        </FormControl>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Cancel
        </Button>
        <Button onClick={handleUpdate} disabled={loading} color="primary">
          {loading ? <CircularProgress size={24} /> : 'Update'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PublishedStatusDialog;
